import React, { useMemo, useRef } from 'react';

import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import type { AwsCredentialsForList } from '@/lib/queries/integrations/cloud/aws/credentials';
import type { AwsRole } from '@/lib/queries/integrations/cloud/aws/roles';

import { useChat } from '../components/providers/ChatProvider';

export const useAwsSyncNotification = ({
  awsUsers,
  awsRoles,
}: {
  awsUsers: AwsCredentialsForList[] | undefined;
  awsRoles: AwsRole[] | undefined;
}): void => {
  const { toast, dismiss } = useToast();
  const { setPrompt } = useChat();
  const previousAwsStatuses = useRef<{
    users: Record<string, string>;
    roles: Record<string, string>;
  }>({ users: {}, roles: {} });

  useMemo(() => {
    const transitionedCredentials: string[] = [];

    // Check users
    awsUsers?.forEach(user => {
      const prevStatus = previousAwsStatuses.current.users[user.id];
      if (
        prevStatus !== undefined &&
        prevStatus === 'inactive' &&
        user.status === 'active'
      ) {
        transitionedCredentials.push(user.name);
      }
      previousAwsStatuses.current.users[user.id] = user.status;
    });

    // Check roles
    awsRoles?.forEach(role => {
      const prevStatus = previousAwsStatuses.current.roles[role.id];
      if (
        prevStatus !== undefined &&
        prevStatus === 'inactive' &&
        role.status === 'active'
      ) {
        transitionedCredentials.push(role.name);
      }
      previousAwsStatuses.current.roles[role.id] = role.status;
    });

    if (transitionedCredentials.length > 0) {
      toast({
        title: 'integrations.aws.syncComplete',
        variant: 'default',
        description: 'integrations.aws.tryIt',
        descriptionValues: {
          button: (parts: React.ReactNode) => (
            <>
              <br />
              <Button
                variant="main"
                className="mt-2"
                onClick={() => {
                  dismiss();
                  setPrompt(
                    'List IAM users with AdministratorAccess policy attached on my AWS accounts',
                  );
                }}
              >
                {parts}
              </Button>
            </>
          ),
        },
      });
    }
  }, [awsUsers, awsRoles, toast, dismiss, setPrompt]);
};
